import { useState, useEffect } from "react";
import { Button, Checkbox, Input, Slider } from "@heroui/react";
import { Icon } from "@iconify/react";
import { isMobile } from "@/stores/MenuStore";
import { fetchWithArgs } from "@/utils/requests";
import { LeagueInfo } from "@/types";
import { Tooltip } from "@heroui/tooltip";
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem } from "@heroui/react";
import CountryFlag from "@/components/country-flags";
import { useCountryStore } from '@/stores/CountriesStore';

export interface StatItem {
    id: number;
    icon: string;
    key: string;
    label: string;
    at_least: number;
}

interface FinderFormProps {
    selectedDates: string[];
    setSelectedDates: (date: any) => void;
    selectedLeagues: string[];
    setSelectedLeagues: (league: string[] | ((prev: string[]) => string[])) => void;
    selectedStat: string;
    setSelectedStat: (stat: string) => void;
    selectedAtLeast: string;
    setSelectedAtLeast: (atLeast: any) => void;
    selectedLastGames: string;
    setSelectedLastGames: (lastGames: string) => void;
    selectedHitRate: number;
    setSelectedHitRate: (hitRate: number) => void;
    getPlayerStats: (dates: string[], leagues: string[], stat: string, atLeast: string, lastGames: string, hitRate: number, statName: string | undefined, leaguesNames: string[] | undefined) => void;
    mode?: 'player' | 'team';
    externalStats?: StatItem[];
}

const FinderForm = function FinderForm(
    {
        selectedDates,
        setSelectedDates,
        selectedLeagues,
        setSelectedLeagues,
        selectedStat,
        setSelectedStat,
        selectedAtLeast,
        setSelectedAtLeast,
        selectedLastGames,
        setSelectedLastGames,
        selectedHitRate,
        setSelectedHitRate,
        getPlayerStats,
        mode = 'player',
        externalStats
    }: FinderFormProps) {
    const { t, i18n } = useTranslation();
    const countryStore = useCountryStore();

    const [dates, setDates] = useState<{ date: string; day: number; month: string; day_week: string }[]>([]);
    const [leagues, setLeagues] = useState<LeagueInfo[]>([]);
    const [groupedLeagues, setGroupedLeagues] = useState<{ country_id: number; leagues: LeagueInfo[] }[]>([]);

    const defaultPlayerStats: StatItem[] = [
        { id: 42, icon: 'game-icons:soccer-kick', key: 'shots_total', label: t('finder_player.finder_player_1'), at_least: 10 },
        { id: 86, icon: 'emojione-monotone:goal-net', key: 'shots_on_target', label: t('finder_player.finder_player_2'), at_least: 10 },
        { id: 80, icon: 'carbon:soccer', key: 'passes', label: t('finder_player.finder_player_3'), at_least: 150 },
        { id: 78, icon: 'material-symbols:shield', key: 'tackles', label: t('finder_player.finder_player_4'), at_least: 10 },
        { id: 56, icon: 'mdi:whistle', key: 'fouls', label: t('finder_player.finder_player_5'), at_least: 10 },
        { id: 96, icon: 'mdi:whistle', key: 'fouls_drawn', label: t('finder_player.finder_player_6'), at_least: 10 },
        { id: 52, icon: 'emojione-monotone:goal-net', key: 'goals', label: t('finder_player.finder_player_7'), at_least: 10 },
        { id: 79, icon: 'ph:handshake-fill', key: 'assists', label: t('finder_player.finder_player_8'), at_least: 10 },
        { id: 100, icon: 'tabler:play-football', key: 'interceptions', label: t('finder_player.finder_player_9'), at_least: 10 },
        { id: 51, icon: 'mdi:flag-off', key: 'offsides', label: t('finder_player.finder_player_10'), at_least: 10 }
    ];

    const stats = externalStats || defaultPlayerStats;
    const lastGames = [
        { key: '3', label: t('finder_player.finder_player_11') },
        { key: '5', label: t('finder_player.finder_player_12') },
        { key: '10', label: t('finder_player.finder_player_13') }
    ];

    const maxAtLeast = stats.find((stat => stat.id.toString() == selectedStat))?.at_least
    const selectedStatName = stats.find((stat => stat.id.toString() == selectedStat))?.label
    const selectedLeaguesName = leagues.filter((league => selectedLeagues.includes(league.id.toString()))).map(league => league.name);

    const toggleDateSelection = (date: string) => {
        setSelectedDates((prevSelected: string[]) =>
            prevSelected.includes(date)
                ? selectedDates.length == 1 ? prevSelected : prevSelected.filter((d: string) => d !== date)
                : [...prevSelected, date]
        );
    };

    useEffect(() => {
        const daysOfWeek = [
            t('finder_player.finder_player_23'),
            t('finder_player.finder_player_24'),
            t('finder_player.finder_player_25'),
            t('finder_player.finder_player_26'),
            t('finder_player.finder_player_27'),
            t('finder_player.finder_player_28'),
            t('finder_player.finder_player_29')
        ];
        const months = [
            t('finder_player.finder_player_30'),
            t('finder_player.finder_player_31'),
            t('finder_player.finder_player_32'),
            t('finder_player.finder_player_33'),
            t('finder_player.finder_player_34'),
            t('finder_player.finder_player_35'),
            t('finder_player.finder_player_36'),
            t('finder_player.finder_player_37'),
            t('finder_player.finder_player_38'),
            t('finder_player.finder_player_39'),
            t('finder_player.finder_player_40'),
            t('finder_player.finder_player_41')
        ];
        const today = new Date();
        const formatDateObject = (date: Date) => {
            const dayOfWeek = daysOfWeek[date.getUTCDay()];
            const day = date.getDate();
            const month = months[date.getMonth()];
            const formattedDate = `${String(day).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
            return {
                date: formattedDate,
                day,
                month,
                day_week: dayOfWeek,
            };
        };
        const upcomingDates = Array.from({ length: 5 }, (_, index) => {
            const nextDate = new Date(today);
            nextDate.setDate(today.getDate() + index);
            return formatDateObject(nextDate);
        });
        setDates(upcomingDates);
    }, [i18n.language]);

    const groupLeaguesByCountry = (leagues: LeagueInfo[]) => {
        const countryMap = new Map<number, { country_id: number; leagues: LeagueInfo[] }>();
        leagues.forEach((league) => {
            const countryId = league.country_id;
            if (!countryMap.has(countryId)) {
                countryMap.set(countryId, { country_id: countryId, leagues: [] });
            }
            countryMap.get(countryId)?.leagues.push(league);
        });
        const sortedCountries = Array.from(countryMap.values()).sort((a, b) => {
            const countryAName = countryStore.countriesById[a.country_id]?.name || '';
            const countryBName = countryStore.countriesById[b.country_id]?.name || '';
            if (!countryAName) return 1;
            if (!countryBName) return -1;
            return countryAName.localeCompare(countryBName);
        });
        setGroupedLeagues(sortedCountries);
    }

    useEffect(() => {
        groupLeaguesByCountry(leagues);
    }, [leagues]);

    useEffect(() => {
        if (selectedDates.length) {
            fetchWithArgs(
                '/api/leagues_by_dates',
                { dates: selectedDates },
                (data) => {
                    setLeagues(data);
                    if (!data.some((league: LeagueInfo) => selectedLeagues.includes(league.id.toString()))) {
                        setSelectedLeagues([]);
                    }
                }
            );
        }
    }, [selectedDates]);

    useEffect(() => {
        if (maxAtLeast && Number(selectedAtLeast) > maxAtLeast) {
            setSelectedAtLeast(maxAtLeast.toString());
        }
    }, [maxAtLeast]);

    const toggleLeagueSelection = (leagueId: string) => {
        setSelectedLeagues((prevSelected: string[]) =>
            prevSelected.includes(leagueId)
                ? prevSelected.filter(id => id !== leagueId)
                : [...prevSelected, leagueId]
        );
    };

    const handleAtLeast = (value: string) => {
        if (maxAtLeast && Number(value) > maxAtLeast) {
            setSelectedAtLeast(maxAtLeast.toString());
        } else if (maxAtLeast && Number(value) < 0) {
            setSelectedAtLeast('0');
        } else setSelectedAtLeast(value);
    };

    return (
        <div className="flex w-full max-w-lg flex-col gap-4 rounded-medium bg-default-50 py-5 shadow-large">
            <h2 className="text-xl font-medium text-default-900 px-4">
                <Icon icon="fluent-emoji-high-contrast:crystal-ball" className="text-2xl mr-2 inline mb-2" />
                {t('finder_player.finder_player_14')}</h2>
            <div className="flex gap-2 flex-col px-5 mt-2">
                <div className="flex gap-1 items-center flex-wrap">
                    <span className="text-md font-medium text-default-600 px-1">{t('finder_player.finder_player_15')}</span>
                    {!selectedDates.length && <Icon className="text-md text-red-500" icon="material-symbols:close" />}
                </div>
                <div className="flex flex-wrap gap-2 items-center justify-center">
                    {dates.map(({ date, day_week, day, month }) => (
                        <Button
                            variant="ghost"
                            key={date}
                            onClick={() => toggleDateSelection(date)}
                            className={`flex h-[80px] bg-default-100 ${selectedDates.includes(date) && "border-primary-500"}`}
                        >
                            <div className="flex flex-col">
                                <span className={`font-medium text-xs text-default-500 ${selectedDates.includes(date) && "text-default-700"}`}>{day_week}</span>
                                <span className={`font-bold text-xl text-default-700 ${selectedDates.includes(date) && "text-default-800"}`}>{`${day < 10 ? `0${day}` : day}`}</span>
                                <span className={`font-medium text-sm text-default-500 ${selectedDates.includes(date) && "text-default-700"}`}>{month}</span>
                            </div>
                        </Button>
                    ))}
                </div>
            </div>
            <div className="flex gap-2 flex-col px-5">
                <div className="flex gap-1 items-center flex-wrap mt-3">
                    <span className="text-md font-medium text-default-600 px-1">{t('finder_player.finder_player_16')}</span>
                    {(selectedDates.length && !selectedLeagues) ? <Icon className="text-md text-red-500" icon="material-symbols:close" /> : null}
                </div>
                <div className="flex flex-wrap gap-3 items-center px-2 max-h-[450px] overflow-y-auto">
                    {!selectedDates.length || !leagues.length
                        ?
                        <div className="flex w-full gap-2 items-center justify-center fadeIn">
                            <Icon className="text-3xl text-default-400" icon="material-symbols:speaker-notes-off-outline" />
                            <span className="text-lg text-default-500">
                                {t('finder_player.finder_player_42')}
                            </span>
                        </div>
                        :
                        <Accordion
                            variant="splitted"
                            selectionMode='multiple'
                        >
                            {groupedLeagues.map(({ country_id, leagues }) => (
                                <AccordionItem
                                    className={"bg-default-100 transition"}
                                    key={country_id}
                                    textValue={`${country_id}`}
                                    keepContentMounted={true}
                                    isCompact={true}
                                    indicator={(
                                        <Icon className="text-default-400 text-xl m-2" icon="mdi:chevron-left" />
                                    )}
                                    title={(
                                        <div className="w-full items-center justify-between gap-2 overflow-x-auto">
                                            <CountryFlag countryId={country_id} />
                                        </div>
                                    )}
                                >
                                    {leagues.map((league) => (
                                        <Checkbox
                                            key={league.id}
                                            isSelected={selectedLeagues.includes(league.id.toString())}
                                            onChange={() => toggleLeagueSelection(league.id.toString())}
                                            size="md"
                                            className="fadeIn mb-0.5"
                                            data-test-id={`${league.id}`}
                                        >
                                            <span className={`text-md me-3 transition ${selectedLeagues.includes(league.id.toString()) ? 'text-default-800' : 'text-default-500'}`}>
                                                {league.name}
                                            </span>
                                        </Checkbox>
                                    ))}
                                </AccordionItem>
                            ))}
                        </Accordion>
                    }
                </div>
            </div>
            <div className="flex gap-2 flex-col px-5">
                <div className="flex gap-1 items-center flex-wrap mt-3">
                    <span className="text-md font-medium text-default-600 px-1">{t('finder_player.finder_player_17')}</span>
                    {(selectedDates.length && selectedLeagues && !selectedStat) ? <Icon className="text-md text-red-500" icon="material-symbols:close" /> : null}
                </div>
                <div className="flex flex-wrap gap-2 items-center justify-center px-2">
                    {stats.map(stat =>
                        <Button
                            key={stat.key}
                            variant="ghost"
                            onClick={() => setSelectedStat(stat.id.toString())}
                            data-test-id={`${stat.key}`}
                            className={`flex w-[45%] bg-default-100 text-default-600 ${selectedStat == stat.id.toString() && "border-primary-500 text-default-800"}`}
                        >
                            <div className="flex w-full items-center justify-center gap-2">
                                <Icon className="text-2xl" icon={stat.icon} />
                                <span className={`flex font-medium w-full justify-center ${isMobile() ? 'text-xs' : 'text-sm'}`}>{stat.label}</span>
                            </div>
                        </Button>
                    )}
                </div>
            </div>
            <div className="flex gap-2 flex-col px-5 mt-3">
                <span className="text-md font-medium text-default-600 px-1">{t('finder_player.finder_player_18')}</span>
                <div className="flex gap-2 items-center px-2">
                    <Button
                        onClick={() => setSelectedAtLeast((prev: any) => String(Math.max(0, parseInt(prev || "0") - 1)))}
                        className="bg-default-100 text-default-500 text-xl font-medium hover:bg-primary-500 hover:text-white disabled:bg-default-50 disabled:text-default-300"
                        isDisabled={!maxAtLeast || Number(selectedAtLeast) <= 0}
                    >
                        {'-'}
                    </Button>
                    <Input
                        type="number"
                        value={selectedAtLeast}
                        onValueChange={(value: string) => handleAtLeast(value)}
                        min={0}
                        max={maxAtLeast}
                        isDisabled={!selectedStat}
                        className="flex w-[80px]"
                        classNames={{
                            input: "pl-3 text-lg",
                        }}
                    />
                    <Button
                        onClick={() => setSelectedAtLeast((prev: any) => String(parseInt(prev || "0") + 1))}
                        className="bg-default-100 text-default-500 text-xl font-medium hover:bg-primary-500 hover:text-white disabled:bg-default-50 disabled:text-default-300"
                        isDisabled={!maxAtLeast || Number(selectedAtLeast) >= maxAtLeast}
                    >
                        {'+'}
                    </Button>
                </div>
            </div>
            <div className="flex gap-2 flex-col px-5">
                <span className="text-md font-medium text-default-600 px-1 mt-3">{t('finder_player.finder_player_19')}</span>
                <div className="flex flex-wrap gap-2 items-center px-2">
                    {lastGames.map(stat =>
                        <Button
                            key={stat.key}
                            variant="ghost"
                            onClick={() => setSelectedLastGames(stat.key)}
                            className={`flex bg-default-100 text-default-600 ${selectedLastGames == stat.key && "border-primary-500 text-default-800"}`}
                        >
                            <span className="flex font-medium">{stat.label}</span>
                        </Button>
                    )}
                </div>
            </div>
            <div className="flex gap-1 flex-col px-5 mt-3">
                <Tooltip content={t('finder_player.finder_player_20')}>
                    <span className="text-md font-medium text-default-600">{t('finder_player.finder_player_21')}</span>
                </Tooltip>
                <div className="flex flex-col justify-center gap-1">
                    <div className="flex items-center justify-end text-default-600 font-medium">{`${(Number(selectedHitRate) * 100)}%`}</div>
                    <Slider
                        arial-label="slider"
                        aria-labelledby="slider"
                        className="max-w-md text-default-600"
                        value={selectedHitRate}
                        onChange={(value: any) => setSelectedHitRate(value)}
                        formatOptions={{ style: "percent" }}
                        maxValue={1}
                        minValue={0}
                        showTooltip={true}
                        step={0.1}
                    />
                </div>
            </div>
            <div className="flex w-full px-5 pt-2">
                <Button
                    className="flex w-full bg-primary-500 text-white font-medium text-md disabled:bg-default-200 disabled:text-default-500"
                    isDisabled={!selectedDates.length || !selectedLeagues || !selectedStat || !Number(selectedAtLeast) || !selectedLastGames || !selectedHitRate}
                    onClick={() => {
                        getPlayerStats(
                            selectedDates, selectedLeagues, selectedStat, selectedAtLeast, selectedLastGames, selectedHitRate, selectedStatName, selectedLeaguesName); window.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });
                    }}
                >
                    {mode === 'team' ? t('finder_team.search_teams') : t('finder_player.finder_player_22')}
                </Button>
            </div>
        </div>
    );
};

export default FinderForm;